import React, { useEffect, useState } from "react";
import axios from "axios";
import "./WeeklyPrizePool.css";

// Reusable Avatar Component with Image Error Handling
const PlayerAvatar = ({ src, name, className, placeholderClass }) => {
  const [imgError, setImgError] = useState(false);
  const initial = name?.trim()?.charAt(0)?.toUpperCase() || "P";

  if (!src || imgError) {
    return (
      <div className={`weekly-avatar-placeholder ${placeholderClass || ""}`}>
        {initial}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={name || "Player"}
      className={className}
      onError={() => setImgError(true)}
    />
  );
};

const WeeklyPrizePool = () => {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        "https://jkr.jokerludo.com/leaderboard/weekly",
      );

      if (response?.data?.status && Array.isArray(response?.data?.data)) {
        setPlayers(response.data.data);
      } else {
        setPlayers([]);
      }
    } catch (error) {
      console.error("Weekly leaderboard error:", error);
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard();

    const interval = setInterval(() => {
      fetchLeaderboard();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN");
  };

  return (
    <section className="weekly-prize-section mt-5">
      <div className="weekly-prize-container">
        {/* HEADER */}
        <div className="weekly-prize-header">
          <div className="weekly-header-glow"></div>

          <div className="weekly-header-left">
            <div className="weekly-trophy-box">
              <span>🏆</span>
            </div>

            <div className="weekly-header-content">
              <div className="weekly-title-row">
                <h2>
                  MONTHLY
                  <span> CHAMPIONS</span>
                </h2>

                <div className="weekly-live-badge">
                  <span className="live-dot"></span>
                  LIVE
                </div>
              </div>

              <p>Top winners of the last 30 days</p>
            </div>
          </div>

          <div className="weekly-period">
            <span className="period-icon">◷</span>
            <div>
              <small>RANKING</small>
              <strong>LAST 30 DAYS</strong>
            </div>
          </div>
        </div>

        {/* LOADING */}
        {loading && (
          <div className="weekly-loading">
            <div className="premium-loader">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <p>Loading champions...</p>
          </div>
        )}

        {/* EMPTY STATE */}
        {!loading && players.length === 0 && (
          <div className="weekly-empty">
            <div className="empty-trophy">🏆</div>
            <h3>The Race Is On</h3>
            <p>Play games, win more and claim your place on the leaderboard.</p>
          </div>
        )}

        {/* LEADERBOARD */}
        {!loading && players.length > 0 && (
          <div className="weekly-leaderboard">
            {/* TOP 3 PODIUM */}
            {players.length >= 3 && (
              <div className="weekly-podium">
                {/* 2ND RANK */}
                <div className="podium-player podium-second">
                  <div className="podium-rank">
                    <span>2</span>
                  </div>

                  <div className="podium-avatar-wrap">
                    <PlayerAvatar
                      src={players[1]?.avatar}
                      name={players[1]?.Name}
                      className="podium-avatar"
                      placeholderClass="podium-avatar placeholder"
                    />
                    <div className="podium-crown">🥈</div>
                  </div>

                  <div className="podium-name">{players[1]?.Name}</div>

                  <div className="podium-winnings">
                    <span>₹</span>
                    {formatAmount(players[1]?.totalWinning)}
                  </div>

                  <div className="podium-wins">
                    {players[1]?.totalWins} Wins
                  </div>
                </div>

                {/* 1ST RANK */}
                <div className="podium-player podium-first">
                  <div className="winner-crown">👑</div>

                  <div className="podium-rank winner-rank">
                    <span>1</span>
                  </div>

                  <div className="podium-avatar-wrap winner-avatar-wrap">
                    <PlayerAvatar
                      src={players[0]?.avatar}
                      name={players[0]?.Name}
                      className="podium-avatar winner-avatar"
                      placeholderClass="podium-avatar placeholder winner-avatar"
                    />
                  </div>

                  <div className="podium-name winner-name">
                    {players[0]?.Name}
                  </div>

                  <div className="podium-winnings winner-winnings">
                    <span>₹</span>
                    {formatAmount(players[0]?.totalWinning)}
                  </div>

                  <div className="podium-wins">
                    {players[0]?.totalWins} Wins
                  </div>
                </div>

                {/* 3RD RANK */}
                <div className="podium-player podium-third">
                  <div className="podium-rank">
                    <span>3</span>
                  </div>

                  <div className="podium-avatar-wrap">
                    <PlayerAvatar
                      src={players[2]?.avatar}
                      name={players[2]?.Name}
                      className="podium-avatar"
                      placeholderClass="podium-avatar placeholder"
                    />
                    <div className="podium-crown">🥉</div>
                  </div>

                  <div className="podium-name">{players[2]?.Name}</div>

                  <div className="podium-winnings">
                    <span>₹</span>
                    {formatAmount(players[2]?.totalWinning)}
                  </div>

                  <div className="podium-wins">
                    {players[2]?.totalWins} Wins
                  </div>
                </div>
              </div>
            )}

            {/* OTHER TOP PLAYERS */}
            <div className="ranking-list-header">
              <div>
                <span className="header-line"></span>
                OTHER TOP PLAYERS
              </div>
              <span>TOTAL WINNINGS</span>
            </div>

            <div className="weekly-player-list">
              {players.slice(players.length >= 3 ? 3 : 0).map((player) => (
                <div className="weekly-player-row" key={player.userId}>
                  <div className="player-rank-number">
                    {String(player.rank).padStart(2, "0")}
                  </div>

                  <div className="weekly-avatar-wrapper">
                    <PlayerAvatar
                      src={player.avatar}
                      name={player.Name}
                      className="weekly-avatar"
                      placeholderClass="weekly-avatar-placeholder"
                    />
                  </div>

                  <div className="weekly-player-info">
                    <div className="weekly-player-name">{player.Name}</div>
                    <div className="weekly-win-count">
                      <span className="mini-win-icon">✦</span>
                      {player.totalWins}{" "}
                      {player.totalWins === 1 ? "Win" : "Wins"}
                    </div>
                  </div>

                  <div className="weekly-winning-amount">
                    <small>WON</small>
                    <div>
                      <span className="weekly-rupee">₹</span>
                      {formatAmount(player.totalWinning)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FOOTER */}
        {!loading && players.length > 0 && (
          <div className="weekly-prize-footer">
            <div className="footer-left">
              <span className="footer-shield">✓</span>
              <span>Rankings based on total winnings</span>
            </div>

            <div className="footer-update">
              <span className="update-dot"></span>
              Auto updated
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default WeeklyPrizePool;
